import { Button, Card, Fade, Grid, Modal, styled, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import MDTypography from "components/MDTypography";
import { useAppContextController } from "context/AppContext";
import { interviewExternalTeamMemberSchema } from "data/interviewExternalTeamMember";
import useYupValidationResolver from "hooks/useYupValidationResolver";
import FormField from "layouts/applications/wizard/components/FormField";
import { isEmpty } from "lodash";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { PatternFormat } from "react-number-format";
import { useQueryClient } from "react-query";
import { getDirtyFields } from "utils/helpers/formHelpers";

const emptyValues = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
};

const StyledTypographyError = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: "0.75rem",
}));

const useStyle = makeStyles({
  container: {
    position: "absolute",
    width: "25%",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    padding: "2rem",
  },
  wideContainer: {
    width: "50%",
  },
  dropzone: {
    height: 170,
  },
  editor: {
    height: 150,
  },
});

const InterviewAddExternalTeamMemberModal = ({
  open,
  setOpen,
  onAdd,
  onEdit,
  currentApplicant,
  currentApplicantIndex,
}) => {
  const { currentLoggedUser } = useAppContextController();
  const { enqueueSnackbar } = useSnackbar();

  const isEdit = !!currentApplicant;

  const classes = useStyle();
  const queryClient = useQueryClient();

  const resolver = useYupValidationResolver(interviewExternalTeamMemberSchema());
  const {
    register,
    handleSubmit,
    trigger,
    clearErrors,
    getValues,
    reset,
    setValue,
    setError,
    control,
    watch,
    Control,
    formState: { errors, isSubmitting, isDirty, dirtyFields },
  } = useForm({ mode: "onBlur", defaultValues: emptyValues, resolver });

  const createApplicantHandler = async (values) => {
    // handleEmailDuplicate(values?.email);
    if (!isEmpty(errors)) return;

    const data = { ...values };
    data.status = "Team Member";
    data.createAgent = currentLoggedUser._id;
    data.notes = [];

    onAdd(data);
    setOpen(false);
  };

  const saveChanges = async (values) => {
    // handleEmailDuplicate(data?.email);
    if (!isEmpty(errors)) return;

    const dFields = getDirtyFields(values, dirtyFields);
    const data = { ...dFields };
    data.status = "Team Member";

    onEdit({
      index: currentApplicantIndex,
      data,
    });
    setOpen(false);
  };

  useEffect(() => {
    clearErrors();
    if (!isEdit) {
      reset(emptyValues);
    } else {
      reset({
        ...emptyValues,
        ...currentApplicant,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, currentApplicant]);

  return (
    <>
      <Modal
        open={open}
        onClose={setOpen}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Fade in={open}>
          <Card className={classes.container}>
            <MDTypography variant="h5" color="dark">
              Add External Team Member
            </MDTypography>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                return handleSubmit(isEdit ? saveChanges : createApplicantHandler)(e);
              }}
            >
              <Grid container>
                <Grid container item>
                  <Grid container xs={12} sx={{ width: "100%", mt: 2 }} spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="firstName"
                        control={control}
                        render={({ field }) => <FormField {...field} label="First Name" />}
                      />
                      {errors?.firstName && (
                        <StyledTypographyError>{errors?.firstName.message}</StyledTypographyError>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="lastName"
                        control={control}
                        render={({ field }) => <FormField label="Last Name" {...field} />}
                      />
                      {errors?.lastName && (
                        <StyledTypographyError>{errors?.lastName.message}</StyledTypographyError>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="phone"
                        control={control}
                        render={({ field }) => (
                          <PatternFormat
                            format="(###) ### ####"
                            mask="_"
                            label="Mobile Phone"
                            {...field}
                            onValueChange={({ value }) => field.onChange(value)}
                            customInput={FormField}
                          />
                        )}
                      />
                      {errors?.phone && (
                        <StyledTypographyError>{errors?.phone.message}</StyledTypographyError>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                          <FormField
                            type="text"
                            label="Email Address"
                            InputLabelProps={{ shrink: getValues("email") }}
                            {...field}
                            onBlur={(e) => {
                              field.onBlur(e.target.value);
                              // handleEmailDuplicate(e.target.value);
                            }}
                          />
                        )}
                      />
                      {errors?.email && (
                        <StyledTypographyError>{errors?.email.message}</StyledTypographyError>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Grid container spacing={3} justifyContent="flex-end" mt={1}>
                <Grid item sm={3}>
                  <Button
                    color="error"
                    variant="contained"
                    fullWidth
                    onClick={(e) => {
                      e?.stopPropagation();
                      setOpen(false);
                    }}
                  >
                    <MDTypography variant="body2" color="white">
                      Cancel
                    </MDTypography>
                  </Button>
                </Grid>
                <Grid item sm={3}>
                  <Button type="submit" variant="contained" fullWidth>
                    <MDTypography variant="body2" color="white">
                      Save
                    </MDTypography>
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Card>
        </Fade>
      </Modal>
    </>
  );
};

export default InterviewAddExternalTeamMemberModal;
